import { z } from "zod";

import {
  EMAIL_MESSAGE,
  MAX_LENGTH_MESSAGE,
  MIN_LENGTH_MESSAGE,
  SELECT_AT_LEAST_ONE_CONTRACT_MESSAGE,
  SELECT_BUSINESS_CLASSIFICATION_MESSAGE,
  SELECT_CNPJ_CARD_MESSAGE,
  SELECT_COMPANY_LOGO_MESSAGE,
  SELECT_CONTRACT_DOCUMENT_MESSAGE,
} from "@/utils";

// Helper para converter undefined/null em string vazia antes da validação
const stringPreprocess = (val: unknown): string => {
  if (val === undefined || val === null) {
    return "";
  }
  return String(val);
};

export const clientPreRegisterFormSchema = z.object({
  name: z.preprocess(
    stringPreprocess,
    z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") }).max(150, { message: MAX_LENGTH_MESSAGE("150") })
  ),
  cnpj: z.preprocess(
    stringPreprocess,
    z.string().min(14, { message: MIN_LENGTH_MESSAGE("14") })
  ),
  corporate_name: z.preprocess(
    stringPreprocess,
    z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") }).max(150, { message: MAX_LENGTH_MESSAGE("150") })
  ),
  email: z.preprocess(
    stringPreprocess,
    z.string().email({ message: EMAIL_MESSAGE })
  ),
  business_classification: z.preprocess(
    stringPreprocess,
    z.string().min(1, {
      message: SELECT_BUSINESS_CLASSIFICATION_MESSAGE,
    })
  ),
  type_partner: z.enum(["client", "business_indicator"]).optional(),
  address: z.object({
    postal_code: z.preprocess(
      stringPreprocess,
      z.string().min(8, { message: MIN_LENGTH_MESSAGE("8") })
    ),
    street: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    number: z.preprocess(
      stringPreprocess,
      z.string().min(1, { message: MIN_LENGTH_MESSAGE("1") })
    ),
    complement: z.preprocess(stringPreprocess, z.string()).optional(),
    neighborhood: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    city: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    state: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
  }),
  company_representative: z.object({
    first_name: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    last_name: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    personal_email: z.preprocess(
      stringPreprocess,
      z.string().email({ message: EMAIL_MESSAGE })
    ),
    cpf: z.preprocess(
      stringPreprocess,
      z.string().min(11, { message: MIN_LENGTH_MESSAGE("11") })
    ),
    mobile_phone: z.preprocess(
      stringPreprocess,
      z.string().min(11, { message: MIN_LENGTH_MESSAGE("11") })
    ),
  }),
  bank_account: z.object({
    bank_name: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    bank_branch: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    checking_account: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    checking_account_digit: z.preprocess(
      stringPreprocess,
      z.string().min(1, { message: MIN_LENGTH_MESSAGE("1") })
    ),
    pix_key: z.preprocess(
      stringPreprocess,
      z.string().max(77, { message: MAX_LENGTH_MESSAGE("77") })
    ),
  }),
  document: z.object({
    company_logo: z.instanceof(File, {
      message: SELECT_COMPANY_LOGO_MESSAGE,
    }),
    cnpj_card: z.instanceof(File, {
      message: SELECT_CNPJ_CARD_MESSAGE,
    }),
    signed_contract: z
      .array(
        z.instanceof(File, {
          message: SELECT_CONTRACT_DOCUMENT_MESSAGE,
        })
      )
      .min(1, {
        message: SELECT_AT_LEAST_ONE_CONTRACT_MESSAGE,
      }),
  }),
});

/** Same as create, but documents are optional (existing partner may keep prior uploads). */
export const clientPartnerPreRegisterEditSchema = clientPreRegisterFormSchema
  .omit({ document: true })
  .merge(
    z.object({
      document: z.object({
        company_logo: z
          .union([z.instanceof(File), z.string().min(1), z.undefined()])
          .optional(),
        cnpj_card: z
          .union([z.instanceof(File), z.string().min(1), z.undefined()])
          .optional(),
        signed_contract: z
          .array(z.union([z.instanceof(File), z.string().min(1)]))
          .optional(),
      }),
    }),
  );

export const clientFormSchemaUpdate = z.object({
  name: z.preprocess(
    stringPreprocess,
    z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") }).max(150, { message: MAX_LENGTH_MESSAGE("150") })
  ),
  cnpj: z.preprocess(
    stringPreprocess,
    z.string().min(14, { message: MIN_LENGTH_MESSAGE("14") })
  ),
  corporate_name: z.preprocess(
    stringPreprocess,
    z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") }).max(150, { message: MAX_LENGTH_MESSAGE("150") })
  ),
  email: z.preprocess(
    stringPreprocess,
    z.string().email({ message: EMAIL_MESSAGE })
  ),
  business_classification: z.preprocess(
    stringPreprocess,
    z.string().min(1, {
      message: SELECT_BUSINESS_CLASSIFICATION_MESSAGE,
    })
  ),
  address: z.object({
    postal_code: z.preprocess(
      stringPreprocess,
      z.string().min(8, { message: MIN_LENGTH_MESSAGE("8") })
    ),
    street: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    number: z.preprocess(
      stringPreprocess,
      z.string().min(1, { message: MIN_LENGTH_MESSAGE("1") })
    ),
    complement: z.preprocess(stringPreprocess, z.string()).optional(),
    neighborhood: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    city: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    state: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
  }),
  company_representative: z.object({
    first_name: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    last_name: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    personal_email: z.preprocess(
      stringPreprocess,
      z.string().email({ message: EMAIL_MESSAGE })
    ),
    cpf: z.preprocess(
      stringPreprocess,
      z.string().min(11, { message: MIN_LENGTH_MESSAGE("11") })
    ),
    mobile_phone: z.preprocess(
      stringPreprocess,
      z.string().min(11, { message: MIN_LENGTH_MESSAGE("11") })
    ),
  }),
  bank_account: z.object({
    bank_name: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    bank_branch: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    checking_account: z.preprocess(
      stringPreprocess,
      z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") })
    ),
    checking_account_digit: z.preprocess(
      stringPreprocess,
      z.string().min(1, { message: MIN_LENGTH_MESSAGE("1") })
    ),
    pix_key: z.preprocess(
      stringPreprocess,
      z.string().max(77, { message: MAX_LENGTH_MESSAGE("77") })
    ),
  }),
  document: z.object({
    company_logo: z.instanceof(File, {
      message: SELECT_COMPANY_LOGO_MESSAGE,
    }),
    cnpj_card: z.instanceof(File, {
      message: SELECT_CNPJ_CARD_MESSAGE,
    }),
    signed_contract: z
      .array(
        z.instanceof(File, {
          message: SELECT_CONTRACT_DOCUMENT_MESSAGE,
        })
      )
      .min(1, {
        message: SELECT_AT_LEAST_ONE_CONTRACT_MESSAGE,
      }),
  }),
});

export const clientProductsFormSchema = z.object({
  planos: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string().optional(),
      plan_features: z
        .array(
          z.string().min(1, { message: "Característica não pode estar vazia" })
        )
        .max(10, { message: "Máximo de 10 características permitidas" }),
      description: z.string().optional(),
      markup: z
        .preprocess(
          (v) => (v === "" || v === null || v === undefined ? undefined : v),
          z.coerce.number().optional()
        )
        .optional(),
      markup_mode: z.enum(["percentage", "real_currency"]).optional(),
      commission_distribution: z
        .array(
          z.object({
            agency: z
              .preprocess(
                (v) => (v === "" || v === null || v === undefined ? 0 : v),
                z.coerce.number()
              )
              .default(0),
            commission: z
              .preprocess(
                (v) => (v === "" || v === null || v === undefined ? 0 : v),
                z.coerce.number()
              )
              .default(0),
            position: z
              .preprocess(
                (v) => (v === "" || v === null || v === undefined ? 0 : v),
                z.coerce.number()
              )
              .default(0),
          })
        )
        .optional(),
      commission_lifetime_other_installments: z
        .preprocess(
          (v) => (v === "" || v === null || v === undefined ? 0 : v),
          z.coerce.number()
        )
        .default(0),
      plan_price: z
        .preprocess(
          (v) => (v === "" || v === null || v === undefined ? undefined : v),
          z.coerce
            .number()
            .min(1, { message: "O preço deve ser maior ou igual a 1" })
            .optional()
        )
        .optional(),
      products: z
        .array(
          z.object({
            id: z.string(),
            name: z.string().optional(),
            type: z.string().optional(),
            product_id: z.string().optional(),
            capital_value: z
              .preprocess(
                (v) => (v === "" || v === null || v === undefined ? undefined : v),
                z.coerce.number().optional()
              )
              .optional(),
            premium_value: z
              .preprocess(
                (v) => (v === "" || v === null || v === undefined ? undefined : v),
                z.coerce.number().optional()
              )
              .optional(),
            description: z.string().optional(),
            minimum_price: z
              .preprocess(
                (v) => (v === "" || v === null || v === undefined ? null : v),
                z.coerce.number().nullable().optional()
              )
              .nullable()
              .optional(),
            plan_name: z.string().nullable().optional(),
            insurance_policy_extra_price_id: z.string().nullable().optional(),
          })
        )
        .min(1, { message: "Pelo menos um produto é obrigatório" }),
      // Campos opcionais para projetos de afinidade
      installments: z.array(
        z.object({
          id: z.string().optional(),
          position: z.number().optional(),
          mainPartner: z.object({
            id: z.string().optional(),
            name: z.string().optional(),
            agency: z.number().optional(),
            commission: z.number().optional(),
          }).optional(),
          participatingPartners: z.array(
            z.object({
              id: z.string().optional(),
              name: z.string().optional(),
              agency: z.number().optional(),
              commission: z.number().optional(),
            })
          ).optional(),
        })
      ).optional(),
      otherInstallments: z.object({
        mainPartner: z.object({
          id: z.string().optional(),
          name: z.string().optional(),
          markup: z.number().optional(),
          commission: z.number().optional(),
        }).optional(),
        participatingPartners: z.array(
          z.object({
            id: z.string().optional(),
            name: z.string().optional(),
            markup: z.number().optional(),
            commission: z.number().optional(),
          })
        ).optional(),
      }).optional(),
      // Hierarquia Comercial
      commercial_hierarchy: z.object({
        project_type: z.enum(['affiliates', 'affinity']).optional(),
        // Se for affiliates
        affiliate_levels: z.array(
          z.object({
            level: z.enum(['N1', 'N2', 'N3']),
            percentage: z
              .preprocess(
                (v) => (v === "" || v === null || v === undefined ? undefined : v),
                z.coerce.number().optional()
              )
              .optional(),
            registration_link: z.string().optional(),
          })
        ).optional(),
        // Se for affinity
        affinity_partner: z.string().optional(),
        // Hunter
        hunter: z.object({
          partner_id: z.string().optional(),
          commission_percentage: z
            .preprocess(
              (v) => (v === "" || v === null || v === undefined ? undefined : v),
              z.coerce.number().optional()
            )
            .optional(),
        }).optional(),
        // Níveis de parceiro
        partner_levels: z.array(
          z.object({
            level: z.string().optional(),
            commission_percentage: z
              .preprocess(
                (v) => (v === "" || v === null || v === undefined ? undefined : v),
                z.coerce.number().optional()
              )
              .optional(),
          })
        ).optional(),
      }).optional(),
    })
  ),
});

export const clientProductsFormSchemaSearch = z.object({
  product_name: z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") }),
  member_name: z.string().min(2, { message: MIN_LENGTH_MESSAGE("2") }),
});

export const rejectionReasonSchema = z.object({
  rejection_reason: z
    .string()
    .min(10, "O motivo deve ter pelo menos 10 caracteres"),
});

export type TRejectionReason = z.infer<typeof rejectionReasonSchema>;

export type TClientProductsFormData = z.infer<typeof clientProductsFormSchema>;

export type TClientPreRegisterFormData = z.infer<
  typeof clientPreRegisterFormSchema
>;

export type TClientFormUpdateData = z.infer<typeof clientFormSchemaUpdate>;

export type TClientProductsFormSearchData = z.infer<
  typeof clientProductsFormSchemaSearch
>;
